import { useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { LayoutDashboard, ClipboardList, KeyRound, Menu, LockKeyhole, Map, CreditCard, Building2, Lock, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { SidebarShell } from "./SidebarShell";
import { CompanyAnnouncement } from "./CompanyAnnouncement";
import { PlatformDashboardAlert } from "./PlatformDashboardAlert";
import { ConfirmEmailScreen } from "@/components/ConfirmEmailScreen";
import { SupportViewBanner } from "./SupportViewBanner";
import { WORKER_ROLE_OPTIONS } from "@/lib/constants";

const NAV = [
  { to: "/manager", label: "Dashboard", icon: LayoutDashboard, end: true, id: "dashboard" },
  { to: "/manager/runs", label: "Warrant Runs", icon: ClipboardList, id: "runs" },
  { to: "/manager/map", label: "Map", icon: Map, id: "map" },
  { to: "/manager/locksmiths", label: "Team", icon: KeyRound, id: "locksmiths" },
  { to: "/manager/lock-changes", label: "Lock Changes", icon: LockKeyhole, id: "lock-changes" },
  { to: "/manager/mortise-locks", label: "Mortise Locks", icon: Lock, id: "mortise-locks" },
  { to: "/manager/partners", label: "Partners", icon: Tag, id: "partners" },
  { to: "/manager/billing", label: "Billing", icon: CreditCard, id: "billing" },
];
const OWNER_NAV = [
  { to: "/manager/admin", label: "Admin", icon: Building2, id: "admin" },
];

const NavItems = ({ onNavigate }) => {
  const { profile } = useAuth();
  const { pathname } = useLocation();
  const enabledTypes = profile?.company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const hide = (i) => (i.id === "map" && profile?.company?.maps_enabled === false) || (i.id === "available" && profile?.company?.basic_plan)
    || (i.id === "lock-changes" && !enabledTypes.includes("locksmith"))
    || (profile?.is_platform_owner && ["billing", "mortise-locks"].includes(i.id));
  const items = (profile?.is_platform_owner ? [...NAV, ...OWNER_NAV] : NAV)
    .flatMap((item) => item.id === "dashboard" && enabledTypes.includes("agent")
      ? [item, { to: "/manager/pre-warrant-visits", label: "Pre Warrant Visits", icon: ClipboardList, id: "pre-warrant-visits" }]
      : [item])
    .filter((item) => !hide(item));
  return (
  <nav className="space-y-1">
    {items.map(({ to, label, icon: Icon, end, id }) => (
      <NavLink
        key={to}
        to={to}
        end={end}
        onClick={onNavigate}
        data-testid={`manager-nav-${id}`}
        className={({ isActive }) => {
          const active = id === "locksmiths" ? pathname === "/manager/locksmiths" : isActive;
          return `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            active ? "bg-blue-800 text-white shadow-sm ring-1 ring-blue-700" : "text-slate-300 hover:bg-slate-800 hover:text-white"
          }`
        }}
      >
        <Icon className="h-4 w-4" />
        {label}
      </NavLink>
    ))}
  </nav>
  );
};

const OPEN_PATHS = ["/manager/admin", "/manager/billing", "/manager/companies", "/manager/owner-account", "/manager/profile"];

const TrialBanner = ({ company, userId }) => {
  if (company.complimentary || ["active", "trialing", "past_due"].includes(company.subscription_status) || !company.trial_ends_at) return null;
  const days = differenceInCalendarDays(parseISO(company.trial_ends_at), new Date());
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="trial-banner">
      <span>{days > 0 ? `Free trial: ${days} day${days === 1 ? "" : "s"} left.` : "Your free trial has ended."} Subscribe to keep using Warrant Whizz.</span>
      {(!company.owner_user_id || company.owner_user_id === userId)
        ? <Link to="/manager/billing" className="font-semibold text-blue-700 hover:underline" data-testid="trial-banner-billing-link">Go to Billing</Link>
        : <span className="text-xs text-blue-800" data-testid="trial-banner-owner-note">Ask your company owner to choose a plan.</span>}
    </div>
  );
};

export default function ManagerLayout() {
  const [open, setOpen] = useState(false);
  const { profile } = useAuth();
  const { pathname } = useLocation();
  const company = profile.company;
  const enabledTypes = company?.enabled_user_types ?? WORKER_ROLE_OPTIONS.map(([role]) => role);
  const locksmithEnabled = enabledTypes.includes("locksmith");
  const label = company?.name || "Manager";
  if (profile.email_verified === false) return <ConfirmEmailScreen />;
  if (company && !company.active && !OPEN_PATHS.includes(pathname) && !pathname.startsWith("/manager/admin/")) return <Navigate to="/manager/billing" replace />;
  if (!locksmithEnabled && pathname === "/manager/lock-changes") return <Navigate to="/manager" replace />;
  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block" data-testid="manager-sidebar">
        <SidebarShell roleLabel={label}>
          <NavItems />
        </SidebarShell>
      </aside>
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-slate-800 bg-slate-900/95 px-4 text-white backdrop-blur-md lg:hidden">
        <Button size="icon" variant="ghost" className="text-white hover:bg-slate-800" onClick={() => setOpen(true)} data-testid="manager-mobile-menu-button" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
        <span className="flex items-center gap-2 font-display font-bold"><img src="/img/logo-icon.png" alt="" className="h-7 w-7 object-contain" />Warrant Whizz</span>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-none p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarShell roleLabel={label} onNavigate={() => setOpen(false)}>
            <NavItems onNavigate={() => setOpen(false)} />
          </SidebarShell>
        </SheetContent>
      </Sheet>
      <main className="lg:pl-64">
        {profile.support_view && <SupportViewBanner view={profile.support_view} />}
        <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
          <PlatformDashboardAlert />
          <CompanyAnnouncement announcement={company?.announcement} />
          {company && <TrialBanner company={company} userId={profile?.id} />}
          {!company && <p className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="no-company-notice">Your account isn&apos;t linked to a company any more. Contact your company&apos;s manager.</p>}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
